import { useState } from 'react';

import { ArrowRight, Clock, CreditCard, Loader2, Sparkles } from 'lucide-react';
import { Trans } from '@lingui/react/macro';

import { CREDIT_PACKAGES } from '~/config/credits';
import type { PackageType } from '~/config/credits';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';

interface PlanDot {
  color: 'dark' | 'gold' | 'transparent';
}

interface PackageGrid {
  cols: number;
  dots: PlanDot[];
}

const PACKAGE_GRIDS: Record<PackageType, PackageGrid> = {
  starter: {
    cols: 1,
    dots: [{ color: 'dark' }, { color: 'dark' }],
  },
  pro: {
    cols: 3,
    dots: [
      { color: 'dark' }, { color: 'dark' }, { color: 'gold' },
      { color: 'dark' }, { color: 'dark' }, { color: 'gold' },
    ],
  },
  advanced: {
    cols: 6,
    dots: [
      { color: 'dark' }, { color: 'dark' }, { color: 'dark' }, { color: 'dark' }, { color: 'gold' }, { color: 'gold' },
      { color: 'dark' }, { color: 'dark' }, { color: 'dark' }, { color: 'dark' }, { color: 'gold' }, { color: 'gold' },
    ],
  },
};

const PACKAGE_CREDIT_TEXT: Record<PackageType, string> = {
  starter: '2 Practice Credits',
  pro: '4 Practice Credits + 2 Free',
  advanced: '8 Practice Credits + 4 Free',
};

function DotGrid({ grid, highlighted }: { grid: PackageGrid; highlighted: boolean }) {
  const rows = Math.ceil(grid.dots.length / grid.cols);
  return (
    <div className="flex flex-col items-center gap-1.5 h-16 justify-center">
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex gap-1.5">
          {Array.from({ length: grid.cols }).map((_, col) => {
            const idx = row * grid.cols + col;
            if (idx >= grid.dots.length) return null;
            const dot = grid.dots[idx];
            let colorClass: string;
            if (dot.color === 'transparent') {
              colorClass = 'bg-transparent';
            } else if (highlighted) {
              colorClass = dot.color === 'gold' ? 'bg-brand-gold' : 'bg-white';
            } else {
              colorClass = dot.color === 'gold' ? 'bg-brand-gold' : 'bg-brand-dark-blue';
            }
            return (
              <div
                key={col}
                className={`w-2.5 h-2.5 rounded-full ${colorClass}`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnUrl: string;
}

export default function CreditPurchaseModal({
  isOpen,
  onClose,
  returnUrl,
}: CreditPurchaseModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState<PackageType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePurchase = async (packageKey: PackageType) => {
    setIsLoading(true);
    setLoadingPackage(packageKey);
    setError(null);

    try {
      const response = await fetch('/api/create-credit-purchase', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package: packageKey, returnUrl }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout');
      }

      if (!data.sessionUrl) {
        throw new Error('No checkout URL returned');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.sessionUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setIsLoading(false);
      setLoadingPackage(null);
    }
  };

  const packages = Object.entries(CREDIT_PACKAGES) as [PackageType, typeof CREDIT_PACKAGES[PackageType]][];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto p-0">
        <div className="p-6 sm:p-8">
          <DialogHeader className="space-y-4 mb-6">
            <DialogTitle className="text-2xl sm:text-3xl font-bold text-brand-gold text-center">
              <Trans>Get Practice Credits</Trans>{' '}
              <span className="inline-block">✨</span>
            </DialogTitle>
            <DialogDescription className="text-center">
              <p className="text-brand-dark-blue/70 text-base">
                <Trans>Choose a credit package to unlock full reports</Trans>
              </p>
            </DialogDescription>
          </DialogHeader>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 mb-8 bg-gray-50 rounded-full px-6 py-3 max-w-2xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4 text-gray-400" />
              <Trans>30-Day Money Back</Trans>
            </div>
            <div className="hidden sm:block w-px h-4 bg-gray-300" />
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Sparkles className="w-4 h-4 text-gray-400" />
              <Trans>Credits Never Expire</Trans>
            </div>
            <div className="hidden sm:block w-px h-4 bg-gray-300" />
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CreditCard className="w-4 h-4 text-gray-400" />
              <Trans>1 Credit = 1 Practice</Trans>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 flex items-center justify-center gap-2 p-3 rounded-lg mb-6">
              <span className="font-medium">{error}</span>
            </div>
          )}

          {/* Package cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {packages.map(([key, pkg]) => {
              const isHighlighted = 'highlighted' in pkg && pkg.highlighted;
              const isPkgLoading = loadingPackage === key;
              const grid = PACKAGE_GRIDS[key];

              return (
                <div
                  key={key}
                  className={`rounded-2xl p-6 sm:p-8 text-center flex flex-col items-center ${
                    isHighlighted
                      ? 'bg-brand-dark-blue text-white shadow-xl sm:scale-105'
                      : 'bg-white border border-gray-200 text-gray-900 shadow-sm'
                  }`}
                >
                  <DotGrid grid={grid} highlighted={isHighlighted} />
                  <h3
                    className={`text-lg font-bold mt-3 mb-4 ${
                      isHighlighted ? 'text-brand-gold' : 'text-brand-dark-blue'
                    }`}
                  >
                    {pkg.name}
                  </h3>
                  <p
                    className={`text-4xl font-bold mb-2 ${
                      isHighlighted ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    ${pkg.price}
                  </p>
                  <p
                    className={`text-sm mb-6 ${
                      isHighlighted ? 'text-white/80' : 'text-gray-500'
                    }`}
                  >
                    {PACKAGE_CREDIT_TEXT[key]}
                  </p>
                  <button
                    onClick={() => handlePurchase(key)}
                    disabled={isLoading}
                    className={`w-full rounded-full py-3 px-6 font-semibold text-sm flex items-center justify-center gap-2 transition-all mt-auto disabled:opacity-50 disabled:cursor-not-allowed ${
                      isHighlighted
                        ? 'bg-white text-brand-dark-blue hover:bg-gray-100'
                        : 'bg-brand-dark-blue text-white hover:bg-brand-dark-blue/90'
                    }`}
                  >
                    {isPkgLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <Trans>Processing...</Trans>
                      </>
                    ) : (
                      <>
                        <Trans>Get Credits</Trans>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
